import { useRollbar } from '@rollbar/react'
import {
  fetchPageDataFailure,
  fetchPageDataSuccess,
} from 'client/actions/pageActions'
import usePresentPage from 'client/hooks/usePresentPage'
import { selectors } from 'client/reducers/pageReducer'
import { useWebsiteSettings } from 'client/store'
import { NetworkError, NotFound } from 'common/errors'
import { AccessDenied } from 'common/errors/AccessDenied'
import { useActions } from 'common/hooks/useActions'
import { changeWebsitePageId, getUrlPathList } from 'common/utils/urlUtils'
import React, { useState } from 'react'
import { useCurrentUrl } from 'src/editor/hooks/use-current-url'
import { useWebsitePagesContent } from 'src/editor/hooks/use-website-pages-content'
import { UnsavedPageModal } from 'src/editor/layout/sidebar/pages-management/components/unsaved-page-modal'
import { fetchWebsitePageData } from 'src/editor/store/website-pages/website-pages-actions'
import { getWebsiteLayoutPageId } from 'src/editor/store/website-settings/website-settings-selector'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'

export const useFetchAndSwitchPage = () => {
  const { t } = useTypedTranslation()
  const rollbar = useRollbar()
  const [loadingPageId, setLoadingPageId] = useState<number | null>(null)
  const [unsavedPageId, setUnsavedPageId] = useState<null | number>(null)
  const currentLayoutPageId = useWebsiteSettings(getWebsiteLayoutPageId)
  const dispatchFetchPageDataFail = useActions(fetchPageDataFailure)
  const dispatchFetchPageData = useActions(fetchPageDataSuccess)
  const dispatchFetchWebsitePage = useActions(fetchWebsitePageData)
  const { getWebsitePageContent } = useWebsitePagesContent()

  const isPageModified = usePresentPage(selectors.isPageModified)

  const currentUrl = useCurrentUrl()
  const urlPageId = getUrlPathList(currentUrl)[3]

  // the second argument is a hack that allows to bypass REDUX value
  // todo refactor in #SC-83061
  const fetchPageData = async (
    pageId: number,
    bypassedLayoutPageId?: number,
  ) => {
    changeWebsitePageId(pageId)
    setLoadingPageId(pageId)
    try {
      const { pageContent, websiteData, layoutPageId, isFromCache } =
        await getWebsitePageContent(
          pageId,
          bypassedLayoutPageId || currentLayoutPageId,
        )

      dispatchFetchPageData(pageContent, rollbar)
      // Only dispatch websiteData if it was a fresh fetch (not from cache)
      if (!isFromCache && websiteData) {
        dispatchFetchWebsitePage({
          ...websiteData,
          layoutPageId,
        })
      }
      setLoadingPageId(null)
    } catch (error) {
      if (error instanceof AccessDenied) {
        // do nothing
      } else if (error instanceof NotFound) {
        dispatchFetchPageDataFail(t('core.errors.page_not_found'))
      } else if (error instanceof NetworkError) {
        dispatchFetchPageDataFail(t('core.errors.no_connection'))
      } else {
        dispatchFetchPageDataFail(t('core.errors.went_wrong_contact_support'))
      }
      setLoadingPageId(null)
    }
  }
  const checkPageModification = (pageId: number, layoutPageId: number) => {
    if (isPageModified) {
      setUnsavedPageId(pageId)
    } else {
      fetchPageData(pageId, layoutPageId)
    }
  }
  const renderUnsavedPageModal = () =>
    unsavedPageId && (
      <UnsavedPageModal
        isVisible={!!unsavedPageId}
        onClose={() => setUnsavedPageId(null)}
        fetchPageData={() => fetchPageData(unsavedPageId as number)}
      />
    )

  return {
    urlPageId,
    isLoading: loadingPageId !== null,
    loadingPageId,
    fetchPageData,
    checkPageModification,
    renderUnsavedPageModal,
  }
}
